import * as THREE from 'three'
import { Side, front, top, right, bottom, left, back } from 'src/constants/sides'
import { getGlobalPlateSide } from 'src/utils/getGlobalPlateSide'
type Vector2 = [number, number]
type Vector3 = [number, number, number]

type LookAt = [0 | 1, 0 | 1, 0 | 1]

type Axis = 'x' | 'y' | 'z'

type Factor = 1 | -1

type PlateDescription = { boundings: THREE.Box3; side: Side }

export const toGlobalTranslation = ({ boundings, side, point }: PlateDescription & { point: Vector2 }): Vector3 => {
  return [1, 1, 1]
}

/**
 * from point of plate to global point
 * default is upperside / front
 *
 * @param param0
 */
export const toGlobalPoint = ({ boundings, side, point }: PlateDescription & { point: Vector2 }): Vector3 => {
  /** bottom left */
  const {
    origin: [x, y, z],
    xAxis,
    xFactor,
    yAxis,
    yFactor
  } = getOriginAndFactor(boundings, side)

  const globalPoint: Vector3 = [x, y, z]
  // x to global
  globalPoint[xAxis === 'x' ? 0 : xAxis === 'y' ? 1 : 2] += xFactor * point[0]
  // y to global
  globalPoint[yAxis === 'x' ? 0 : yAxis === 'y' ? 1 : 2] += yFactor * point[1]

  return globalPoint
}

export const fromGlobalPoint = ({ boundings, side, point }: PlateDescription & { point: Vector3 }): Vector2 => {
  const { origin, xAxis, xFactor, yAxis, yFactor } = getOriginAndFactor(boundings, side)
  const platePoint: Vector2 = [0, 0]
  // get corresponding Value on axis
  const xIndex = xAxis === 'x' ? 0 : xAxis === 'y' ? 1 : 2
  const yIndex = yAxis === 'x' ? 0 : yAxis === 'y' ? 1 : 2
  platePoint[0] = (point[xIndex] - origin[xIndex]) * xFactor
  platePoint[1] = (point[yIndex] - origin[yIndex]) * yFactor
  return platePoint
}

// helper

const getOriginAndFactor = (
  boundings: THREE.Box3,
  side: Side
): {
  origin: [number, number, number]
  xAxis: Axis
  yAxis: Axis
  xFactor: Factor
  yFactor: Factor
} => {
  switch (side) {
    case front:
      return {
        origin: [
          boundings.min.x,
          boundings.min.y,
          boundings.max.z // front
        ],
        xAxis: 'x',
        yAxis: 'y',
        xFactor: 1,
        yFactor: 1
      }
    case top:
      return {
        origin: [
          boundings.max.x,
          boundings.max.y, // front
          boundings.max.z
        ],
        xAxis: 'z',
        xFactor: -1,
        yAxis: 'x',
        yFactor: -1
      }
    case right:
      return {
        origin: [
          boundings.max.x,
          boundings.min.y, // front
          boundings.max.z
        ],
        xAxis: 'z',
        xFactor: -1,
        yAxis: 'y',
        yFactor: 1
      }
    case bottom:
      return {
        origin: [
          boundings.min.x,
          boundings.min.y, // front
          boundings.max.z
        ],
        xAxis: 'z',
        xFactor: -1,
        yAxis: 'x',
        yFactor: 1
      }
    case left:
      return {
        origin: [
          boundings.min.x,
          boundings.min.y, // front
          boundings.min.z
        ],
        xAxis: 'z',
        xFactor: 1,
        yAxis: 'y',
        yFactor: 1
      }
    case back:
      return {
        origin: [
          boundings.max.x,
          boundings.min.y,
          boundings.min.z // front
        ],
        xAxis: 'x',
        xFactor: -1,
        yAxis: 'y',
        yFactor: 1
      }
  }
}
